import { useCallback, useMemo } from 'react';
import { Tooltip, Box, Typography } from '@mui/material';
import { StatusChip } from './ui';
import {
  buildRegistrationPaymentHoverLines,
  buildRegistrationStatusHoverContent,
  synthesizeBookingPrintReceipt
} from '../utils/registrationStatusHover.js';
import {
  openAdminReceiptPrintEnriched,
  resolveBookingForReceiptPrint
} from '../utils/registrationReceiptView.js';
import { displayReceiptNumber } from '../utils/displayReceiptNumber.js';
import { razorpayDashboardUrl } from '../utils/razorpayDashboardUrl.js';
import { useToast } from '../context/ToastContext.jsx';

function findReceiptForHoverLine(booking, line) {
  const receipts = Array.isArray(booking?.receipts) ? booking.receipts.filter(Boolean) : [];
  if (!receipts.length) return null;

  const lineId = String(line?.id || '');
  if (lineId.startsWith('br-')) {
    const receiptId = lineId.slice(3);
    const byId = receipts.find((r) => String(r.id) === receiptId);
    if (byId) return byId;
  }
  if (lineId.startsWith('lr-')) {
    const legacyId = lineId.slice(3);
    const linked = receipts.find((r) => String(r.receiptId ?? r.receipt_id ?? '') === legacyId);
    if (linked) return linked;
  }

  const receiptNo = String(line?.receiptNo || '').trim();
  if (receiptNo && receiptNo !== '—') {
    const byNo = receipts.find((r) => displayReceiptNumber(r) === receiptNo);
    if (byNo) return byNo;
  }

  if (line?.printReceipt) {
    const printId = line.printReceipt?.id;
    if (printId != null) {
      const byPrint = receipts.find((r) => String(r.id) === String(printId));
      if (byPrint) return byPrint;
    }
  }

  return receipts[0];
}

function HoverSection({ title, children }) {
  if (!children) return null;
  return (
    <Box sx={{ mb: 1.25, '&:last-child': { mb: 0 } }}>
      <Typography
        variant="caption"
        component="div"
        sx={{ fontWeight: 700, color: '#111827', mb: 0.5, fontSize: '0.8rem' }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function PaymentHoverLine({ line, onViewError }) {
  const paymentToken = String(line.paymentId || '').trim();
  const paymentUrl = line.paymentLinkUrl || razorpayDashboardUrl(paymentToken);
  const detailPlain = String(line.detail || '').trim();
  const showDetailPlain = detailPlain && detailPlain !== paymentToken;
  const participantLabel = String(line.participantLabel || '').trim();

  const handleView = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const listBooking = line.booking;
      let receipt = line.printReceipt;

      if (!receipt?.id && listBooking) {
        const booking = await resolveBookingForReceiptPrint(listBooking);
        const freshLines = buildRegistrationPaymentHoverLines(booking);
        const freshLine = freshLines.find((l) => l.id === line.id) || freshLines[0];
        receipt =
          findReceiptForHoverLine(booking, freshLine || line) ||
          line.printReceipt ||
          synthesizeBookingPrintReceipt(booking);
      }

      if (!receipt) {
        onViewError?.(new Error('NO_RECEIPT'));
        return;
      }

      await openAdminReceiptPrintEnriched({ ...receipt, booking: listBooking });
    } catch (err) {
      onViewError?.(err);
    }
  };

  return (
    <Box component="li" sx={{ listStyle: 'disc' }}>
      {participantLabel ? (
        <Typography
          component="div"
          variant="body2"
          sx={{ fontSize: 'inherit', color: '#111827', fontWeight: 600, mb: 0.25, wordBreak: 'break-word' }}
        >
          {participantLabel}
        </Typography>
      ) : null}
      <Typography component="div" variant="body2" sx={{ fontSize: 'inherit', color: '#1f2937', wordBreak: 'break-word' }}>
        {line.method} ({line.receiptNo}
        {paymentToken ? (
          <>
            /{' '}
            {paymentUrl ? (
              <a
                href={paymentUrl}
                target="_blank"
                rel="noopener"
                className="reg-status-hover-view"
                onClick={(e) => e.stopPropagation()}
                title="Open in Razorpay dashboard"
              >
                {paymentToken}
              </a>
            ) : (
              paymentToken
            )}
          </>
        ) : showDetailPlain ? (
          `/ ${detailPlain}`
        ) : null}
        ) — {line.amountLabel}
      </Typography>
      <a href="#" className="reg-status-hover-view" onClick={handleView} style={{ display: 'inline-block', marginTop: 2 }}>
        View receipt
      </a>
    </Box>
  );
}

function HoverBody({ content, onViewError }) {
  const { paymentLines, commentLines } = content;
  return (
    <Box className="reg-status-hover-body" onClick={(e) => e.stopPropagation()}>
      <HoverSection title="Payment Details">
        {paymentLines.length ? (
          <Box component="ul" sx={{ m: 0, pl: 2.25, '& li': { mb: 0.35, fontSize: '0.8rem', lineHeight: 1.45 } }}>
            {paymentLines.map((line) => (
              <PaymentHoverLine key={line.id} line={line} onViewError={onViewError} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            No payments recorded yet.
          </Typography>
        )}
      </HoverSection>

      {commentLines.length ? (
        <HoverSection title="Comments">
          <Box component="ul" sx={{ m: 0, pl: 2.25, '& li': { mb: 0.35, fontSize: '0.8rem', lineHeight: 1.45 } }}>
            {commentLines.map((line, i) => (
              <Box component="li" key={`${line.kind}-${i}`}>
                {line.author ? (
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit' }}>
                    {line.author}
                    {line.when ? ': ' : ' — '}
                  </Typography>
                ) : null}
                <Typography component="span" variant="body2" sx={{ fontSize: 'inherit', color: '#374151' }}>
                  {line.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </HoverSection>
      ) : null}
    </Box>
  );
}

export default function RegistrationStatusHover({
  booking,
  traveler = null,
  travelerIndex = 0,
  status,
  statusLabel,
  badgeClass: _badgeClass
}) {
  const { showToast } = useToast();
  const content = useMemo(
    () => buildRegistrationStatusHoverContent(booking, traveler, travelerIndex),
    [booking, traveler, travelerIndex]
  );
  const handleViewError = useCallback(
    (err) => {
      if (err?.message === 'POPUP_BLOCKED') {
        showToast({
          message: 'Please allow pop-ups to open the receipt.',
          severity: 'warning'
        });
        return;
      }
      if (err?.message === 'NO_RECEIPT') {
        showToast({
          message: 'No receipt found for this payment.',
          severity: 'warning'
        });
        return;
      }
      showToast({
        message: 'Failed to open receipt.',
        severity: 'error'
      });
    },
    [showToast]
  );
  const tooltipTitle = useMemo(
    () => <HoverBody content={content} onViewError={handleViewError} />,
    [content, handleViewError]
  );

  return (
    <Tooltip
      enterDelay={250}
      leaveDelay={80}
      placement="top"
      arrow
      title={tooltipTitle}
      slotProps={{
        popper: {
          sx: { zIndex: 1500 },
          modifiers: [{ name: 'offset', options: { offset: [0, -6] } }]
        },
        tooltip: {
          className: 'reg-status-hover-tooltip',
          sx: {
            maxWidth: 520,
            bgcolor: '#fff',
            color: '#111827',
            border: '1px solid #e5e7eb',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            p: 1.25,
            pointerEvents: 'auto'
          }
        },
        arrow: {
          sx: { color: '#fff', '&::before': { border: '1px solid #e5e7eb' } }
        }
      }}
    >
      <span className="reg-status-hover-trigger" tabIndex={0}>
        <StatusChip
          status={status}
          label={statusLabel}
          sx={{
            cursor: 'help',
            textDecoration: 'underline dotted',
            textUnderlineOffset: 3
          }}
        />
      </span>
    </Tooltip>
  );
}
